import React, { useState, useMemo, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Drawer as MuiDrawer, List, ListItem, ListItemIcon, ListItemText, useTheme, styled, Collapse, Box, Menu, MenuItem, Badge, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import { tokens } from "../../theme";
import DrawerHeader from "./DrawerHeader";
import DrawerFooter from "./DrawerFooter";
import { useMenuItems } from './MenuItems';
import { AuthContext } from "../../Context/AuthContext";
import PeopleIcon from '@mui/icons-material/People';
import smartCvLogo from "../../Assets/logo.svg";


const drawerWidth = 220;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  '& .MuiListItemText-primary': {
    overflow: 'visible',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  }
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const StyledListItem = styled(ListItem)(({ theme, colors, isActive }) => ({
  margin: "4px 12px",
  padding: "8px 8px",
  borderRadius: "8px",
  width: "auto",
  background: isActive ? `linear-gradient(90deg, ${colors.primary[400]} 0%, ${colors.primary[300]} 100%)` : "transparent",
  color: isActive ? colors.grey[50] : colors.grey[700],
  "&:hover": {
    backgroundColor: isActive ? colors.primary[300] : theme.palette.action.hover,
  },
  "& .MuiListItemIcon-root": {
    color: isActive ? colors.grey[50] : colors.grey[700],
    minWidth: "40px",
  },
  "& .MuiListItemText-root": {
    "& .MuiTypography-root": {
      whiteSpace: "normal",
      wordWrap: "break-word",
      hyphens: "auto"
    }
  },
  "& .css-tw23s2-MuiBadge-root .MuiBadge-dot": {
    backgroundColor: isActive ? colors.grey[50] : colors.grey[700],
  },
}));

const ChevronIcon = styled(ExpandMoreRoundedIcon)(({ open }) => ({
  transition: 'transform 0.2s',
  transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
}));

const StepperCircle = styled(Box)(({ theme, $active }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: $active ? theme.palette.primary.main : theme.palette.text.disabled,
  marginRight: theme.spacing(1),
}));

const NestedListItem = styled(ListItem)(({ theme, selected, colors }) => ({
  padding: theme.spacing(0.5, 2),
  marginLeft: "12px",
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiListItemText-primary': {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    hyphens: 'auto'
  }
}));

const StyledListItemText = styled(ListItemText)(({ selected, colors, theme }) => ({
  padding: theme.spacing(0.5, 2),
  background: selected ? `linear-gradient(90deg, ${colors.primary[100]} 0%, ${colors.primary[100]} 100%)` : "transparent",
  color: selected ? colors.primary[600] : colors.grey[700],
  margin: "4px 12px",
  // padding: "8px 8px",
  borderRadius: "8px",
  width: "auto",
  "& .MuiTypography-root": {
    whiteSpace: "normal",
    wordWrap: "break-word",
    hyphens: "auto"
  }
}));
const StyledMenuItem = styled(MenuItem)(({ theme, colors, $active }) => ({
  color: $active ? colors.primary[600] : colors.grey[700],
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "& .MuiListItemIcon-root": {
    color: $active ? colors.grey[50] : colors.grey[700],
  },
  "& .MuiTypography-root": {
    whiteSpace: "normal",
    wordWrap: "break-word",
    hyphens: "auto",
    maxWidth: "200px"
  }
}));

// Add the toggle button component
const ToggleButton = styled(IconButton)(({ theme, colors, open }) => ({
  position: 'fixed',
  left: open ? `${drawerWidth - 20}px` : '49px', // Dynamically position based on sidebar state
  top: '70px', // Position at top, near the header
  zIndex: 1200,
  background: theme.palette.background.paper,
  color: colors.grey[700],
  width: '30px',
  height: '30px',
  borderRadius: '50%',
  border: `1px solid ${colors.grey[300]}`,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  transition: theme.transitions.create(['left'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  '&:hover': {
    background: theme.palette.background.default,
  },
}));

const SideBar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const menuItems = useMenuItems();
  const { ability } = useContext(AuthContext);
  const [smartCvDialogOpen, setSmartCvDialogOpen] = useState(false);

  const handleSmartCvClick = () => setSmartCvDialogOpen(true);
  const handleSmartCvClose = () => setSmartCvDialogOpen(false);
  const handleSmartCvConfirm = () => {
    window.open('https://smart-cv.worldws.pro/dashboard', '_blank', 'noopener,noreferrer');
    setSmartCvDialogOpen(false);
  };

  const cardStyles = {
    '& .MuiDrawer-paper': {
      color: colors.grey[700],
    },
  };

  const toggle = () => setOpen(!open);

  const isActive = (path) => location.pathname === path;

  // Function to check if an item is accessible
  const isItemAccessible = (item) => {
    if (!item) return false;

    // If it's a direct route without children
    if (!item.children) {
      // If the item exists in MenuItems.jsx without any ability check, treat it as accessible
      return true;
    }

    // If it has children, check if any children are accessible
    return item.children.some(child => child && isItemAccessible(child));
  };

  const handleExpandClick = (itemText) => {
    setExpandedItems(prev =>
      prev.includes(itemText)
        ? prev.filter(item => item !== itemText)
        : [...prev, itemText]
    );
  };

  const handleMenuOpen = (event, item) => {
    if (!open && item.children) {
      setMenuAnchor({
        vertical: event.currentTarget.getBoundingClientRect().top,
        horizontal: event.currentTarget.getBoundingClientRect().right,
        target: event.currentTarget
      });
      setActiveMenuItem(item);
    }
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveMenuItem(null);
    setHoveredItem(null);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    handleMenuClose();
  };

  // Style de menu personnalisé pour gérer les textes longs
  const StyledMenu = styled(Menu)(({ theme }) => ({
    '& .MuiPaper-root': {
      maxWidth: '200px',
      overflowX: 'hidden'
    }
  }));

  const MenuItem = React.memo(({ item, level = 0 }) => {
    const active = isActive(item.path) || (item.children && item.children.some((child) => isActive(child.path)));
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.text);

    // Check if this item is accessible
    if (!isItemAccessible(item)) {
      return null;
    }

    // Check if there are any accessible children
    const accessibleChildren = hasChildren ? item.children.filter(child => child && isItemAccessible(child)) : [];
    
    // If it has children but none are accessible, don't render
    if (hasChildren && accessibleChildren.length === 0) {
      return null;
    }

    const handleItemClick = (e) => {
      if (!open && hasChildren) {
        e.stopPropagation();
        setHoveredItem(item);
        setMenuAnchor({
          vertical: e.currentTarget.getBoundingClientRect().top,
          horizontal: e.currentTarget.getBoundingClientRect().right,
          target: e.currentTarget
        });
        setActiveMenuItem(item);
      } else {
        if (hasChildren && open) {
          handleExpandClick(item.text);
        }
        navigate(item.path);
      }
    };

    return (
      <>
        {!open ? (
          <Tooltip title={item.text} placement="right">
            <StyledListItem
              component="div"
              isActive={active}
              colors={colors}
              onClick={handleItemClick}
              sx={{
                pl: level * 2 + 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                paddingY: '4px',
              }}
              aria-current={active ? 'page' : undefined}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon>
                  {(!open && hasChildren) ? (
                    <Badge
                      sx={{
                        "& .MuiBadge-dot": {
                          backgroundColor: colors.grey[500]
                        }
                      }}
                      badgeContent=" "
                      variant="dot"
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      invisible={accessibleChildren.length === 0}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>

                {open && <ListItemText primary={item.text} />}
              </div>
              {hasChildren && open && accessibleChildren.length > 0 && (
                <ChevronIcon open={isExpanded} fontSize="small" className="ml-auto" />
              )}
            </StyledListItem>
          </Tooltip>
        ) : (
          <StyledListItem
            component="div"
            isActive={active}
            colors={colors}
            onClick={handleItemClick}
            sx={{
              pl: level * 2 + 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              paddingY: '4px',
            }}
            aria-current={active ? 'page' : undefined}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ListItemIcon>
                {(!open && hasChildren) ? (
                  <Badge
                    sx={{
                      "& .MuiBadge-dot": {
                        backgroundColor: colors.grey[500]
                      }
                    }}
                    badgeContent=" "
                    variant="dot"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    invisible={accessibleChildren.length === 0}
                  >
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>

              {open && <ListItemText primary={item.text} />}
            </div>
            {hasChildren && open && accessibleChildren.length > 0 && (
              <ChevronIcon open={isExpanded} fontSize="small" className="ml-auto" />
            )}
          </StyledListItem>
        )}

        {hasChildren && open && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {accessibleChildren.map((child) => (
                <NestedListItem
                  colors={colors}
                  key={child.text}
                  onClick={() => navigate(child.path)}
                  selected={isActive(child.path)}
                  sx={{
                    paddingY: '0px',
                    marginY: '0px',
                  }}
                >
                  <StepperCircle $active={isActive(child.path)} />
                  <StyledListItemText
                    primary={child.text}
                    colors={colors}
                    selected={isActive(child.path)}
                    sx={{
                      paddingY: '4px',
                    }}
                  />
                </NestedListItem>
              ))}
            </List>
          </Collapse>
        )}

        {!open && activeMenuItem && hoveredItem === item && (
          <StyledMenu
            className="sidebar-menu"
            anchorEl={menuAnchor?.target}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            anchorReference="anchorPosition"
            anchorPosition={
              menuAnchor
                ? { top: menuAnchor.vertical, left: menuAnchor.horizontal }
                : undefined
            }
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              style: {
                marginLeft: '8px',
                maxWidth: '200px',
                overflowX: 'hidden'
              }
            }}
            MenuListProps={{
              onMouseLeave: handleMenuClose
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {accessibleChildren.map((child) => (
              <StyledMenuItem
                key={child.text}
                onClick={() => handleMenuItemClick(child.path)}
                colors={colors}
                $active={isActive(child.path)}
              >
                <StepperCircle $active={isActive(child.path)} />
                <StyledListItemText 
                  primary={child.text} 
                  selected={isActive(child.path)} 
                  colors={colors}
                />
              </StyledMenuItem>
            ))}
          </StyledMenu>
        )}
      </>
    );
  });

  return (
    <Box sx={{ position: 'relative', display: 'flex' }}>
      <Drawer variant="permanent" open={open} sx={{
        ...cardStyles,
        '& .MuiDrawer-paper': {
          ...cardStyles['& .MuiDrawer-paper'],
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          height: '100%',
          overflowX: 'hidden',
          overflowY: 'hidden'
        }
      }}>
        <DrawerHeader open={open} toggle={toggle} />
        
        {/* Menu items list */}
        <List sx={{ 
          flexGrow: 1, 
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingBottom: '60px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {menuItems.map((item) => (
            <MenuItem key={item.text} item={item} />
          ))}
        </List>
        {/* Smart CV external link (bottom pinned) */}
        <Box sx={{ width: '100%', boxSizing: 'border-box', pb: 1 }}>
          {!open ? (
            <Tooltip title="Smart CV" placement="right">
              <StyledListItem
                component="div"
                isActive={false}
                colors={colors}
                onClick={handleSmartCvClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  paddingY: '4px',
                  borderTop: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  mt: 0.5,
                  mb: 0.5,
                }}
              >
                <ListItemIcon>
                  <img src={smartCvLogo} alt="Smart CV" style={{ width: 25, height: 25 }} />
                </ListItemIcon>
              </StyledListItem>
            </Tooltip>
          ) : (
            <StyledListItem
              component="div"
              isActive={false}
              colors={colors}
              onClick={handleSmartCvClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                paddingY: '4px',
                borderTop: 1,
                borderBottom: 1,
                borderColor: 'divider',
                mt: 0.5,
                mb: 0.5,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon>
                  <img src={smartCvLogo} alt="Smart CV" style={{ width: 25, height: 25 }} />
                </ListItemIcon>
                {open && <ListItemText primary="Smart CV" />}
              </div>
            </StyledListItem>
          )}
        </Box>
        {/* Confirmation dialog for Smart CV */}
        <Dialog open={smartCvDialogOpen} onClose={handleSmartCvClose} maxWidth="xs" fullWidth>
          <DialogTitle>Open Smart CV?</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              You are about to open Smart CV in a new browser tab: https://smart-cv.worldws.pro/dashboard
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSmartCvClose}>Cancel</Button>
            <Button variant="contained" onClick={handleSmartCvConfirm}>Continue</Button>
          </DialogActions>
        </Dialog>
        
        <DrawerFooter open={open} />
      </Drawer>
      
      {/* Toggle button removed - now using only the one next to the title */}
    </Box>
  );
};

export default SideBar;
